import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, Signal, signal, viewChild, WritableSignal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { UntilDestroy } from '@ngneat/until-destroy';
import { iif, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';

import { MarkFormAsTouchedIfInvalid, StringHelper } from 'tiime-expert-utils';
import {
  DialogBase,
  TiimeAlertModule,
  TiimeButtonModule,
  TiimeDialogModule,
  TiimeFormModule,
  TiimeIconModule,
  TiimeSnackbarService,
  TiimeTooltipModule
} from 'tiime-material';

import { UserApiService } from '@api-services/user-api.service';
import { UserForm } from '@forms/user/user-form';
import { User } from '@models/user';
import { CreateUserDialogData } from '@shared-dialogs/create-user-dialog/create-user-dialog-data';
import { CreateUserFormComponent } from '@shared-forms/create-user-form/create-user-form.component';
import { PhonePipe } from '@shared-pipes/phone/phone.pipe';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-create-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TiimeFormModule,
    TiimeDialogModule,
    TiimeButtonModule,
    TiimeTooltipModule,
    TiimeIconModule,
    TiimeAlertModule,
    CreateUserFormComponent,
    PhonePipe
  ],
  templateUrl: './create-user-dialog.component.html',
  styleUrls: ['./create-user-dialog.component.scss']
})
export class CreateUserDialogComponent extends DialogBase<CreateUserDialogComponent> implements OnInit {
  readonly createUserFormComponent: Signal<CreateUserFormComponent> =
    viewChild.required<CreateUserFormComponent>(CreateUserFormComponent);

  readonly userForm: UserForm = new UserForm();
  readonly overlayData: Signal<CreateUserDialogData> = signal(inject(MAT_DIALOG_DATA)).asReadonly();
  readonly emailAlreadyUsed: WritableSignal<boolean> = signal<boolean>(false);
  readonly existingUsers: WritableSignal<User[]> = signal<User[]>([]);

  readonly existingUsersMessage: Signal<string> = computed(() => {
    if (this.existingUsers().length === 0) {
      return ``;
    }

    if (this.existingUsers().length === 1) {
      return `Un contact correspond au téléphone ou à l'email fourni.`;
    }

    if (this.existingUsers().length > 1) {
      return `Plusieurs contacts correspondent au téléphone ou à l'email fourni.`;
    }
  });

  private suggestedUser: boolean;

  private readonly subscription: Subscription = new Subscription();

  constructor(
    dialogRef: MatDialogRef<CreateUserDialogComponent>,
    private readonly userApiService: UserApiService,
    private readonly snackbarService: TiimeSnackbarService
  ) {
    super(dialogRef);
  }

  ngOnInit(): void {
    this.observeFormChange();
  }

  selectExistingUser(user: User): void {
    // On garde la valeur actuelle du formulaire pour pouvoir
    // la restaurer en cas de problème lors de la sauvegarde
    const savedUser: User = this.userForm.toUser();
    this.createUserFormComponent().resetUserFormWithUserSuggestion(user);
    this.suggestedUser = true;

    this.handleUserCreation(user)
      .pipe(
        catchError(() => {
          this.createUserFormComponent().resetUserFormWithUserSuggestion(savedUser);
          return of();
        })
      )
      .subscribe();
  }

  @MarkFormAsTouchedIfInvalid('userForm')
  submitUserForm(): void {
    const user = this.userForm.toUser(false);
    this.userForm.disable();

    iif(
      () => this.suggestedUser || (!this.suggestedUser && !!user.id),
      this.handleUserCreation(user),
      this.checkIfUserExist(user).pipe(
        switchMap((existingUsers: User[]) => {
          if (existingUsers.length === 0) {
            return this.handleUserCreation(user);
          }
          return of(existingUsers);
        })
      )
    )
      .pipe(finalize(() => this.userForm.enable()))
      .subscribe();
  }

  private checkIfUserExist(user: User): Observable<User[]> {
    return this.userApiService
      .checkIfEmailOrPhoneExist(user)
      .pipe(tap((existingUsers: User[]) => this.setExistingUsers(existingUsers, user.email)));
  }

  private setExistingUsers(existingUsers: User[], userEmail: string): void {
    this.existingUsers.set(existingUsers);
    if (existingUsers?.length) {
      this.snackbarService.openError(this.existingUsersMessage());

      if (userEmail?.length) {
        const emailValue = StringHelper.normalizeToLowerCase(userEmail);
        const index = existingUsers.findIndex(
          ({ email }) => email && StringHelper.normalizeToLowerCase(email) === emailValue
        );
        this.emailAlreadyUsed.set(index > -1);
        // Si l'email est déjà utilisé, on place cet utilisateur en premier dans la liste
        if (this.emailAlreadyUsed() && index !== 0) {
          existingUsers.unshift(existingUsers.splice(index, 1)[0]);
          this.existingUsers.set(existingUsers);
        }
      }
    }
  }

  private handleUserCreation(user: User): Observable<User> {
    return iif(
      () => !!user.id,
      this.createRelation(user.id).pipe(switchMap(() => this.updateUser(user))),
      this.createUser(user)
    ).pipe(
      tap((newUser: User) => this.dialogRef.close(newUser ? newUser : user)),
      tap(() => this.snackbarService.openSuccess('Le contact a bien été ajouté')),
      catchError(err => {
        this.suggestedUser = false;
        return throwError(err);
      })
    );
  }

  private observeFormChange(): void {
    this.subscription.add(
      this.userForm.valueChanges
        .pipe(
          // On sérialise la valeur pour éviter de faire une comparaison de référence
          map<any, string>(value => JSON.stringify(value)),
          distinctUntilChanged(),
          filter(() => !!this.existingUsers().length),
          tap(() => {
            this.existingUsers.set([]);
            this.emailAlreadyUsed.set(false);
          })
        )
        .subscribe()
    );
  }

  private createUser(user: User): Observable<User> {
    return this.userApiService.createUser(user, this.overlayData().companyId);
  }

  private updateUser(user: User): Observable<User> {
    return this.userApiService.updateUser(user, this.overlayData().companyId);
  }

  private createRelation(userId: number): Observable<void> {
    return this.userApiService.createRelation(userId, this.overlayData().companyId);
  }
}
